import React, { useState } from 'react';
import { 
  Monitor, Network, Shield, Building2, GraduationCap, 
  Smartphone, Server, Cloud, MessageSquare, ShoppingCart,
  Laptop, TrendingUp, Lock, Database, Boxes, Tv, HardDrive,
  Factory, Heart, Banknote, BookOpen, Store, Cpu, 
  Mail, Globe, Search, Wrench
} from 'lucide-react';
import { servicesData } from '../data/mock';

const Services = () => {
  const [activeCategory, setActiveCategory] = useState(0);

  const categoryIcons = {
    "Hardware Solutions": Monitor,
    "Security & Infrastructure": Shield,
    "Industry-Specific Solutions": Building2,
    "Consulting & Services": TrendingUp
  };

  const serviceIcons = {
    "Thin Client & Mini PC": Laptop,
    "Workstation": Cpu,
    "POS System": ShoppingCart,
    "Industrial PC & Panel PC": Factory,
    "Rack & Tower Server": Server,
    "Enterprise Storage": Database,
    "Digital Signage": Tv,
    "Kiosk & Interactive Kiosk": Monitor,
    "Firewall & DLP Solutions": Shield,
    "Antivirus & Endpoint Solutions": Lock,
    "Network Security & Threat Management Audit": Search,
    "Healthcare IT Solutions": Heart,
    "Banking & NBFC Solutions": Banknote,
    "Education Solutions": GraduationCap,
    "Retail Solutions": Store,
    "Industrial Solutions": Factory,
    "IT Infrastructure AMC": Wrench,
    "IT Consulting Solutions": TrendingUp,
    "Chatbot Solutions": MessageSquare,
    "Website & App Development": Globe,
    "Cloud Platform Solutions": Cloud,
    "Email Solutions": Mail
  };

  return (
    <section id="services" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-cyan-100 rounded-full mb-4">
            <span className="text-cyan-700 font-semibold text-sm">Our Solutions</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {servicesData.title}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {servicesData.subtitle}
          </p>
        </div>

        {/* Service Image Banner */}
        <div className="mb-16 rounded-2xl overflow-hidden shadow-2xl">
          <div className="relative h-64 md:h-80">
            <img 
              src={servicesData.image} 
              alt="IT Infrastructure"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-900/80 to-lime-900/60 flex items-center justify-center">
              <h3 className="text-white text-3xl md:text-4xl font-bold text-center px-4">
                Comprehensive IT Solutions for Every Need
              </h3>
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {servicesData.categories.map((category, index) => {
            const IconComponent = categoryIcons[category.category];
            return (
              <button
                key={index}
                onClick={() => setActiveCategory(index)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  activeCategory === index
                    ? 'bg-gradient-to-r from-cyan-500 to-lime-500 text-white shadow-lg scale-105'
                    : 'bg-white text-gray-700 border border-gray-200 hover:border-cyan-500 hover:text-cyan-600'
                }`}
              >
                {IconComponent && <IconComponent size={20} />}
                <span>{category.category}</span>
              </button>
            );
          })}
        </div>

        {/* Services Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {servicesData.categories[activeCategory].items.map((service, index) => {
            const IconComponent = serviceIcons[service.name] || Monitor;
            return (
              <div
                key={index}
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 group"
              >
                <div className="mb-4 p-3 bg-gradient-to-br from-cyan-100 to-lime-100 rounded-lg inline-block group-hover:scale-110 transition-transform duration-300">
                  <IconComponent className="text-cyan-700" size={28} />
                </div>
                
                {service.title && (
                  <div className="mb-3">
                    <p className="text-xs font-bold text-cyan-600 uppercase tracking-wider mb-1">
                      {service.title}
                    </p>
                    {service.subtitle && (
                      <p className="text-sm font-semibold text-gray-500 uppercase">
                        {service.subtitle}
                      </p>
                    )}
                  </div>
                )}
                
                <h4 className="text-lg font-bold text-gray-900 mb-3">
                  {service.name}
                </h4>
                
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  {service.description}
                </p>
                
                {service.specs && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <ul className="space-y-2">
                      {service.specs.map((spec, specIndex) => (
                        <li key={specIndex} className="flex items-start text-xs text-gray-600">
                          <span className="text-cyan-500 mr-2">▪</span>
                          <span>{spec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* All Services Count */}
        <div className="mt-16 text-center">
          <div className="inline-block bg-gradient-to-r from-cyan-50 to-lime-50 rounded-2xl p-8 border border-cyan-200">
            <div className="text-5xl font-bold bg-gradient-to-r from-cyan-600 to-lime-600 bg-clip-text text-transparent mb-2">
              22+ Service Categories
            </div>
            <p className="text-gray-700 text-lg">
              Comprehensive solutions covering all aspects of IT infrastructure and services
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
